/**
 * 보고서 발송 (운영자 › 브랜드·계정) — 회차마다 카톡으로 보낼 **보고서 이미지 + 안내문**을 업체별로 만든다
 * (261020-report-message).
 *
 * ★ 문구는 서버가 조립한다 — 숫자가 보고서 이미지와 같아야 해서 같은 계산(`cardInputOf`·`headlineOf`)을 쓴다.
 *   화면은 고쳐 쓰고 복사만 한다. 고친 내용은 저장하지 않는다(다시 불러오면 서버 문구로 돌아간다).
 * ★ 대상은 **그 회차에 작업한 업체**뿐이다 — 빠진 곳은 맨 아래 이름만 보인다.
 * ★ 이미지는 누를 때만 그린다 — 업체 수만큼 한꺼번에 그리면 서버가 수십 초를 쓴다(장당 1~2초).
 * ★ 「보냄」 표시는 이 브라우저에만 남는다(회차·업체 단위) — 누가 보냈는지 공유하는 기록이 아니다.
 * ★ 본문의 링크는 **결제 링크 하나**다 — 카톡이 그 링크로 미리보기를 그린다. 서버 문구에는 자리(`PAY_LINK_SLOT`)만 있고,
 *   여기서 카드 주인 계정을 골라 링크를 만들면 그 자리가 주소로 바뀐다(261020-pay-link — 주소는 발급 응답에 한 번만 나온다).
 */
import { billing, registry, reportMessage } from '@geo-admin/shared/contract/api/index.ts';
import {
  PAY_LINK_SLOT,
  type ReportMessageBrand,
  type ReportMessageQuery,
  type ReportMessageSubscription,
} from '@geo-admin/shared/contract/api/reportMessage.ts';
import { useEffect, useState } from 'react';
import { CyclePicker } from '../components/CyclePicker.tsx';
import { PageTitle } from '../components/PageTitle.tsx';
import { ReportDispatchAdmin } from '../components/ReportDispatchAdmin.tsx';
import { BrandKindTabs, useBrandKind } from '../components/BrandKindTabs.tsx';
import { Button } from '../components/ui/button.tsx';
import { Badge, Card, CardBody, CardHeader } from '../components/ui/card.tsx';
import { Select, Textarea } from '../components/ui/form.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi, useApiMutation } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';

const md = (iso: string) => `${Number(iso.slice(5, 7))}/${Number(iso.slice(8, 10))}`;
const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;

/** <img>·<a> 는 헤더를 못 실어 쿼리로 운영자 영역을 알린다(`BrandReportCardAdmin` 과 같다) */
const cardUrl = (brandId: string, back: number, download: boolean) =>
  `/api/brands/${brandId}/report-card.png?cycle=${back}&realm=admin${download ? '' : '&download=0'}`;

const sentKey = (label: string, brandId: string) => `report-message:sent:${label}:${brandId}`;
const readSent = (key: string) => {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};
const writeSent = (key: string, on: boolean) => {
  try {
    if (on) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
  } catch {
    // 저장소를 못 쓰면 표시만 이 화면에 남는다
  }
};

const STATUS_LABEL: Record<string, string> = { active: '이용 중', confirmed: '결제 대기', pending: '승인 대기' };

export function ReportMessagesPage() {
  // 0 = **업체마다** 어제가 든 회차(구독일이 업체마다 달라 서버가 업체별로 센다, 261020-report-cycle-yesterday-basis)
  const [back, setBack] = useState(0);
  // 「실제 | 테스트」 탭 — 테스트 브랜드는 실제 발송 목록에 섞지 않는다(261023-test-brand)
  const brandKind = useBrandKind();
  // 「자동 발송」(261030-report-auto-dispatch) — 구독 기간 끝날에 서버가 보낸 기록. 「직접 보내기」는 이 화면의 원래 일이다
  const [tab, setTab] = useState<'manual' | 'auto'>('manual');
  const q = useApi(
    reportMessage.listReportMessages,
    { query: { cycle: back, brandKind } },
    { enabled: tab === 'manual' },
  );
  const brands = q.data?.brands ?? [];
  const cycle = brands[0]?.cycle;

  return (
    <>
      <PageTitle
        title="보고서 발송"
        description="그 회차에 작업한 업체마다 보고서 이미지와 카톡 안내문(언급률 처음 → 지금 → 3개월 차 예상 · 결제 안내)을 만듭니다. 결제 링크를 만들어 넣고 복사하세요"
        actions={tab === 'manual' ? <CyclePicker cycle={cycle} back={back} onBack={setBack} /> : null}
      />
      <BrandKindTabs />
      <div className="flex gap-1 border-slate-200 border-b">
        {(
          [
            ['manual', '직접 보내기'],
            ['auto', '자동 발송'],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === k ? 'border-slate-900 font-semibold text-slate-900' : 'border-transparent text-slate-500'}`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'auto' ? <ReportDispatchAdmin /> : null}
      {tab === 'manual' && q.isLoading ? <Loading label="업체별 문구를 만드는 중" /> : null}
      {tab === 'manual' && q.error ? <ErrorBox error={q.error} /> : null}
      {tab === 'manual' && q.data ? (
        <>
          <p className="text-sm text-slate-600">
            대상 <b>{brands.length}곳</b>
            {q.data.skipped.length ? ` · 작업 내역이 없어 빠진 곳 ${q.data.skipped.length}곳` : ''}
          </p>
          {brands.map((b) => (
            <BrandMessage key={`${brandKind}:${back}:${b.brandId}`} brand={b} />
          ))}
          {q.data.skipped.length ? (
            <p className="text-slate-500 text-xs">
              빠진 곳(이 회차 진행 내역 없음): {q.data.skipped.map((s) => s.brandName).join(', ')}
            </p>
          ) : null}
        </>
      ) : null}
    </>
  );
}

function BrandMessage({ brand: b }: { brand: ReportMessageBrand }) {
  // 이미지는 **그 업체의 회차 번호**로 부른다 — 화면의 넘김 값(`fromOpen`)과 다르다
  const back = b.back;
  const [payUrl, setPayUrl] = useState<string | null>(null);
  const base = payUrl ? b.message.replace(PAY_LINK_SLOT, payUrl) : b.message;
  const [text, setText] = useState(base);
  const [preview, setPreview] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const key = sentKey(b.cycle.label, b.brandId);
  const [sent, setSent] = useState(() => readSent(key));
  useEffect(() => setText(b.message), [b.message]);
  const needsLink = b.message.includes(PAY_LINK_SLOT);

  const onIssued = (url: string) => {
    setPayUrl(url);
    if (text.includes(PAY_LINK_SLOT)) {
      setText(text.replace(PAY_LINK_SLOT, url));
      setNotice('결제 링크를 문구에 넣었습니다');
    } else setNotice(`문구에 ${PAY_LINK_SLOT} 자리가 없습니다 — 링크를 직접 넣어 주세요: ${url}`);
  };

  const copy = async () => {
    if (text.includes(PAY_LINK_SLOT) && !confirm('결제 링크를 아직 만들지 않았습니다. 그대로 복사할까요?')) return;
    try {
      await navigator.clipboard.writeText(text);
      setNotice('복사했습니다');
    } catch {
      setNotice('복사하지 못했습니다 — 글을 직접 선택해 복사하세요');
    }
  };
  const toggleSent = () => {
    writeSent(key, !sent);
    setSent(!sent);
  };
  const s = b.subscription;

  return (
    <Card className={sent ? 'opacity-60' : undefined}>
      <CardHeader
        title={b.brandName}
        description={`${b.cycle.label} · ${md(b.cycle.from)}~${md(b.cycle.to)}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={b.brandStatus === 'active' ? 'green' : 'amber'}>
              {STATUS_LABEL[b.brandStatus] ?? b.brandStatus}
            </Badge>
            {s && s.price.supplyKrw > 0 ? (
              <Badge tone="blue">
                월 {won(s.price.amountKrw)} · {s.hasCard ? '카드 등록됨' : '카드 없음'}
              </Badge>
            ) : (
              <Badge tone="red">구독 금액 없음 — 결제 안내 빠짐</Badge>
            )}
          </div>
        }
      />
      <CardBody className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <div className="grid content-start gap-3">
          <QueryFacts queries={b.queries} />
          <div className="flex flex-wrap gap-2">
            <a
              href={cardUrl(b.brandId, back, true)}
              className="inline-flex h-8 items-center justify-center rounded-md bg-slate-900 px-3 font-medium text-sm text-white hover:bg-slate-700"
            >
              보고서 이미지 받기
            </a>
            <Button size="sm" variant="outline" onClick={() => setPreview((v) => !v)}>
              {preview ? '미리보기 닫기' : '이미지 미리보기'}
            </Button>
          </div>
          {preview ? (
            <img
              src={cardUrl(b.brandId, back, false)}
              alt={`${b.brandName} ${b.cycle.label} 보고서 이미지`}
              className="w-full max-w-[320px] rounded-lg border border-slate-200"
            />
          ) : null}
        </div>
        <div className="grid content-start gap-2">
          {needsLink && s ? <PayLinkIssue brandId={b.brandId} sub={s} issued={payUrl} onIssued={onIssued} /> : null}
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={Math.min(28, text.split('\n').length + 1)}
            className="font-[inherit] leading-relaxed"
            aria-label={`${b.brandName} 안내문`}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => void copy()}>
              문구 복사
            </Button>
            {text !== base ? (
              <Button size="sm" variant="ghost" onClick={() => setText(base)}>
                처음 문구로
              </Button>
            ) : null}
            <label className="ml-auto flex items-center gap-1.5 text-sm text-slate-600">
              <input type="checkbox" checked={sent} onChange={toggleSent} />
              보냄
            </label>
            {notice ? <span className="w-full text-slate-500 text-xs">{notice}</span> : null}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * 결제 링크 만들기 — 카드 주인이 될 소속 계정을 골라 발급한다(`billing.tsx` 의 `PayLinkAdmin` 과 같은 계약).
 * 같은 구독에 열린 링크가 있으면 새로 만들 때 옛 링크가 죽는다 — 이미 보낸 링크면 그 고객의 주소가 끊긴다
 */
function PayLinkIssue({
  brandId,
  sub,
  issued,
  onIssued,
}: {
  brandId: string;
  sub: ReportMessageSubscription;
  issued: string | null;
  onIssued: (url: string) => void;
}) {
  const members = useApi(registry.listBrandMembersAdmin, { params: { id: brandId } });
  const issue = useApiMutation(billing.issuePayLink);
  const list = members.data?.members ?? [];
  const owner = list.find((m) => m.role === 'owner');
  const [userId, setUserId] = useState('');
  const chosen = userId || owner?.userId || '';
  const make = () => {
    if (
      sub.openPayLink &&
      !confirm(
        `이미 열린 결제 링크(${sub.openPayLink.userName} · ${dateTime(sub.openPayLink.expiresAt)}까지)가 있습니다. 새로 만들면 그 링크는 더는 열리지 않습니다.`,
      )
    )
      return;
    issue.mutate({ params: { id: sub.id }, body: { userId: chosen } }, { onSuccess: (r) => onIssued(r.url) });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs">
      <span className="font-medium text-amber-900">결제 링크</span>
      {issued ? (
        <span className="text-slate-700">만들었습니다 — 72시간 동안 열립니다</span>
      ) : (
        <>
          {members.isLoading ? <span className="text-slate-500">계정 불러오는 중…</span> : null}
          {members.data && list.length === 0 ? (
            <span className="text-bad">소속 계정이 없습니다 — 구독·결제 화면에서 계정을 먼저 소속시키세요</span>
          ) : null}
          {list.length > 0 ? (
            <>
              <span className="text-slate-600">카드 주인</span>
              <Select className="h-8 w-auto bg-white" value={chosen} onChange={(e) => setUserId(e.target.value)}>
                {list.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.name ?? m.companyName ?? m.email} · {m.email} ({m.role === 'owner' ? '소유자' : '관리자'})
                  </option>
                ))}
              </Select>
              <Button size="sm" onClick={make} disabled={!chosen || issue.isPending}>
                {issue.isPending ? '만드는 중…' : '결제 링크 만들어 넣기'}
              </Button>
            </>
          ) : null}
          {issue.error ? <span className="w-full text-bad">{(issue.error as Error).message}</span> : null}
        </>
      )}
    </div>
  );
}

/** 문구에 들어간 숫자의 근거 — 운영자가 보내기 전에 확인한다 */
function QueryFacts({ queries }: { queries: ReportMessageQuery[] }) {
  if (queries.length === 0) return <p className="text-slate-500 text-sm">측정 질문이 없습니다.</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-slate-500 text-xs">
          <th className="py-1 font-medium">질문</th>
          <th className="py-1 font-medium">처음</th>
          <th className="py-1 font-medium">지금</th>
          <th className="py-1 font-medium">실측(온보딩 끝)</th>
        </tr>
      </thead>
      <tbody>
        {queries.map((q) => (
          <tr key={q.text} className="border-slate-100 border-t align-top">
            <td className="py-1.5 pr-2">{q.text}</td>
            <td className="py-1.5 pr-2 whitespace-nowrap">{q.start ? `${q.start.rate}% (${md(q.start.on)})` : '—'}</td>
            <td className="py-1.5 pr-2" title={q.now?.caption}>
              {q.now ? `${q.now.rate}%` : '—'}
            </td>
            <td className="py-1.5 whitespace-nowrap">
              {q.actual !== null ? `${q.actual}%` : '—'}
              {q.onboardingEndsOn ? ` (${md(q.onboardingEndsOn)})` : ' (온보딩 아님)'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
